<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { toast } from 'dashboard/composables/useToast';
import { useLoadWithRetry } from 'dashboard/composables/loadWithRetry';
import { useMessageContext } from '../provider.js';
import Icon from 'next/icon/Icon.vue';
import StickerFavoritesAPI from 'dashboard/api/stickerFavorites';

const { t } = useI18n();
const { attachments, status } = useMessageContext();
const inProgress = computed(() => status?.value === 'progress');

const attachment = computed(() => attachments.value[0]);

const { isLoaded, hasError, loadWithRetry } = useLoadWithRetry({
  max_retry: 4,
  backoff: 800,
});

const contextMenuVisible = ref(false);
const contextMenuStyle = ref({});
const savedStickerId = ref(null);
const checkingFavorite = ref(false);

const displayUrl = computed(
  () => attachment.value?.thumbUrl || attachment.value?.dataUrl
);

onMounted(() => {
  if (displayUrl.value) {
    loadWithRetry(displayUrl.value, attachment.value?.id);
  }
});

watch(displayUrl, newUrl => {
  if (newUrl && !isLoaded.value && !hasError.value) {
    loadWithRetry(newUrl, attachment.value?.id);
  }
});

const closeContextMenu = () => {
  contextMenuVisible.value = false;
};

const showContextMenu = async event => {
  contextMenuStyle.value = {
    top: `${event.clientY}px`,
    left: `${event.clientX}px`,
  };
  contextMenuVisible.value = true;
  checkingFavorite.value = true;
  setTimeout(() => document.addEventListener('click', closeContextMenu), 0);

  try {
    const { data } = await StickerFavoritesAPI.check(attachment.value.id);
    savedStickerId.value = data.id;
  } catch {
    savedStickerId.value = null;
  } finally {
    checkingFavorite.value = false;
  }
};

onUnmounted(() => {
  document.removeEventListener('click', closeContextMenu);
});

const saveToFavorites = async () => {
  contextMenuVisible.value = false;
  try {
    const { data } = await StickerFavoritesAPI.saveFromAttachment(
      attachment.value.id
    );
    savedStickerId.value = data.id;
    toast.success(t('CONVERSATION.DINASTIA_ACTIONS.STICKER.SAVED'));
  } catch {
    toast.error(t('CONVERSATION.DINASTIA_ACTIONS.STICKER.SAVE_FAILED'));
  }
};

const removeFromFavorites = async () => {
  contextMenuVisible.value = false;
  try {
    await StickerFavoritesAPI.delete(savedStickerId.value);
    savedStickerId.value = null;
    toast.success(t('CONVERSATION.DINASTIA_ACTIONS.STICKER.DELETED'));
  } catch {
    toast.error(t('CONVERSATION.DINASTIA_ACTIONS.STICKER.DELETE_FAILED'));
  }
};
</script>

<template>
  <div class="max-w-[100px]" data-bubble-name="sticker">
    <!-- Loading skeleton -->
    <div
      v-if="!isLoaded && !(hasError && !inProgress)"
      class="w-[100px] h-[100px] bg-n-alpha-2 rounded-lg animate-pulse flex items-center justify-center"
    >
      <Icon
        icon="i-lucide-sticker"
        class="size-8 text-n-slate-9 animate-bounce"
      />
    </div>
    <!-- Error state -->
    <div
      v-else-if="hasError && !inProgress"
      class="flex items-center gap-1 text-n-slate-11"
    >
      <Icon icon="i-lucide-circle-off" />
      <p class="mb-0">
        {{ t('COMPONENTS.MEDIA.IMAGE_UNAVAILABLE') }}
      </p>
    </div>
    <!-- Loaded sticker -->
    <img
      v-else
      :src="displayUrl"
      class="max-w-[100px] max-h-[100px] object-contain cursor-pointer"
      @contextmenu.prevent="showContextMenu"
    />

    <!-- Context menu -->
    <Teleport to="body">
      <div
        v-if="contextMenuVisible"
        class="fixed z-[999] bg-white dark:bg-n-solid-3 rounded-lg shadow-lg border border-n-weak dark:border-n-strong overflow-hidden min-w-[180px]"
        :style="contextMenuStyle"
      >
        <!-- Loading state while checking -->
        <div
          v-if="checkingFavorite"
          class="flex items-center gap-2 px-4 py-2.5 text-sm text-n-slate-11"
        >
          <Icon icon="i-lucide-loader-2" class="size-4 animate-spin" />
          ...
        </div>
        <!-- Remove button (red) -->
        <button
          v-else-if="savedStickerId"
          class="flex items-center gap-2 px-4 py-2.5 w-full text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
          @click="removeFromFavorites"
        >
          <Icon icon="i-lucide-trash-2" class="size-4" />
          {{ t('CONVERSATION.DINASTIA_ACTIONS.STICKER.REMOVE_FAVORITE') }}
        </button>
        <!-- Save button (green) -->
        <button
          v-else
          class="flex items-center gap-2 px-4 py-2.5 w-full text-left text-sm text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
          @click="saveToFavorites"
        >
          <Icon icon="i-lucide-star" class="size-4" />
          {{ t('CONVERSATION.DINASTIA_ACTIONS.STICKER.SAVE_FAVORITE') }}
        </button>
      </div>
    </Teleport>
  </div>
</template>
